/**
 * Browser compatibility layer.
 *
 * Three deliberately independent concerns:
 *
 * 1. installCompatShims() - polyfills Array.prototype.at / String.prototype.at so
 *    third-party scripts (notably PostHog's lazily-loaded web-vitals extension) and
 *    our own code don't throw `TypeError: x.at is not a function` on legacy engines.
 *    `.at()` shipped in Chrome 92 / Firefox 90 / Safari 15.4 (mid-2021); older
 *    clients - in practice scrapers/bots pinning an ancient Chrome UA - lack it.
 *    Ref: Sentry CODEPULSE-BACKEND-C4 (Chrome 79 hitting web-vitals.js on `/`).
 *
 * 2. installTranslationDomGuard() - hardens Node.prototype.removeChild / insertBefore
 *    against third-party DOM mutation (in practice Chrome / Google Translate, which
 *    wraps text nodes in <font> elements and reparents them out from under React).
 *    When React's commit phase then tries to remove/insert a node whose live parent
 *    has changed, the native call throws an uncaught `NotFoundError: Failed to
 *    execute 'removeChild' ... not a child of this node` that crashes the page.
 *    The guard turns that into a safe no-op / append so a translated DOM degrades
 *    gracefully instead of taking down the app.
 *    Ref: Sentry CODEPULSE-BACKEND-CK (Italian-locale Chrome on /onboarding/select-repos).
 *
 * 3. isModernBrowser() - feature-detects whether the client is a browser we actually
 *    support, so callers can skip analytics / session replay on legacy/bot clients
 *    and keep that traffic out of our free-tier quota. It probes features we do NOT
 *    shim (structuredClone, Object.hasOwn) on purpose, so the polyfill above can
 *    never mask a genuinely unsupported browser.
 */

function atImpl(this: { length: number; [index: number]: unknown }, index: number): unknown {
  let n = Math.trunc(index) || 0;
  if (n < 0) n += this.length;
  if (n < 0 || n >= this.length) return undefined;
  return this[n];
}

// `.at` / `Object.hasOwn` are ES2022; the project targets `lib: ES2020`, so they
// are not in the type lib. Access them through these casts rather than widening
// the global lib (which would let the rest of the app assume APIs legacy clients lack).
type MaybeHasAt = { at?: unknown };

function definePrototypeAt(proto: object): void {
  Object.defineProperty(proto, 'at', {
    value: atImpl,
    writable: true,
    configurable: true,
    enumerable: false, // match native: must not show up in for...in
  });
}

/**
 * Install polyfills for engines older than our baseline. Idempotent: native
 * implementations are left untouched. Call once, as early as possible (main.tsx),
 * before any third-party script that may rely on these methods runs.
 */
export function installCompatShims(): void {
  if (typeof (Array.prototype as MaybeHasAt).at !== 'function') {
    definePrototypeAt(Array.prototype);
  }
  if (typeof (String.prototype as MaybeHasAt).at !== 'function') {
    definePrototypeAt(String.prototype);
  }
}

// Marker stamped on our patched DOM methods so installTranslationDomGuard() is
// idempotent and never wraps a method twice (which would compound the overhead).
const GUARD_FLAG = '__codepulseTranslationGuard';

type GuardableMethod = { [GUARD_FLAG]?: true };

/**
 * Harden Node.prototype.removeChild / insertBefore so that browser-injected DOM
 * mutation (Google Translate / Chrome auto-translate reparenting text nodes) can't
 * crash React's commit phase with an uncaught NotFoundError.
 *
 * - removeChild: if the node's live parent is no longer `this`, the browser already
 *   detached/reparented it, so there is nothing for us to remove - return the node
 *   instead of throwing. React's intent (the node is gone from `this`) is satisfied.
 * - insertBefore: if the reference node's live parent is no longer `this`, fall back
 *   to appending to `this` (insertBefore with null) so the new node still lands in
 *   the container rather than throwing.
 *
 * Idempotent. Call once, as early as possible (main.tsx), before React renders.
 */
export function installTranslationDomGuard(): void {
  if (typeof Node === 'undefined' || !Node.prototype) return;
  const proto = Node.prototype;
  if ((proto.removeChild as GuardableMethod)[GUARD_FLAG]) return; // already patched

  const originalRemoveChild = proto.removeChild;
  function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Node was reparented/detached by a third-party script (e.g. translation).
      // Nothing to remove from `this`; returning the node mirrors the native API
      // contract without throwing.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  }
  (guardedRemoveChild as GuardableMethod)[GUARD_FLAG] = true;
  proto.removeChild = guardedRemoveChild as typeof proto.removeChild;

  const originalInsertBefore = proto.insertBefore;
  function guardedInsertBefore<T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was reparented out from under us; append instead of throwing
      // so the new node still ends up inside the intended container.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  }
  (guardedInsertBefore as GuardableMethod)[GUARD_FLAG] = true;
  proto.insertBefore = guardedInsertBefore as typeof proto.insertBefore;
}

/**
 * True when the client supports the modern-JS baseline CodePulse targets.
 *
 * Probes `structuredClone` (Chrome 98 / FF 94 / Safari 15.4) and `Object.hasOwn`
 * (Chrome 93 / FF 92 / Safari 15.4) - both absent in the legacy/bot engines that
 * generate web-vitals noise (e.g. Chrome 79). These are intentionally NOT covered
 * by installCompatShims(), so a shimmed `.at` can't make a legacy browser look
 * modern here.
 */
export function isModernBrowser(): boolean {
  if (typeof window === 'undefined') return false;
  const hasStructuredClone = typeof structuredClone === 'function';
  const hasObjectHasOwn = typeof (Object as { hasOwn?: unknown }).hasOwn === 'function';
  return hasStructuredClone && hasObjectHasOwn;
}
